import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type {
  LoginRequest,
  LoginResponse,
  LoginResult,
  UserDto,
} from '@aiai/shared';
import { fetchAccounts, login as loginRequest, logoutRequest, refreshSession } from '../api';
import {
  adoptSlotOwner,
  currentAppPath,
  getSlot,
  handleOwnerChanged,
  setSlotOwner,
  stripSlot,
} from '../lib/accountSlot';
import { bootRedirect, isPublicPath } from '../lib/accountRouting';
import { getTokenExpiryMs, setToken } from '../lib/token';
import { setUnauthorizedHandler } from '../lib/api';
import { setActiveOrgId } from '../lib/activeOrg';

export interface AuthContextValue {
  user: UserDto | null;
  /** True while we still have a token but haven't resolved /auth/me yet. */
  initializing: boolean;
  isAuthenticated: boolean;
  /**
   * Step 1 login. Returns the raw result: when `requiresTotp`, the caller must
   * complete the TOTP step and then call setSession; otherwise the session is
   * already adopted here.
   */
  login: (payload: LoginRequest) => Promise<LoginResult>;
  /** Adopt a session from a login/setup response (auto-login). */
  setSession: (res: LoginResponse) => void;
  logout: () => void;
  /** Sign out of every account in this browser. */
  logoutAll: () => void;
}

// The context object lives here beside its provider; `useAuth` consumes it. The
// non-component export trips react-refresh's heuristic, which is harmless here.
// eslint-disable-next-line react-refresh/only-export-components
export const AuthContext = createContext<AuthContextValue | null>(null);

// Refresh this long before the access token's own `exp`, so an open run view
// never has to round-trip through a 401 in the common case.
const REFRESH_BUFFER_MS = 60_000;
const MIN_REFRESH_DELAY_MS = 5_000;

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [user, setUser] = useState<UserDto | null>(null);
  const [initializing, setInitializing] = useState<boolean>(true);
  const refreshTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelScheduledRefresh = useCallback(() => {
    if (refreshTimer.current) {
      clearTimeout(refreshTimer.current);
      refreshTimer.current = null;
    }
  }, []);

  const clearSession = useCallback(() => {
    cancelScheduledRefresh();
    setToken(null);
    setUser(null);
    setSlotOwner(null);
    // Clear the stored org so the next login doesn't send a stale X-Org-Id
    // header, which would cause OrgContextGuard to 403 on GET /orgs.
    setActiveOrgId(null);
    queryClient.clear();
  }, [cancelScheduledRefresh, queryClient]);

  // Proactively refresh ~60s before expiry — a self-rescheduling timer, so
  // the session renews itself for as long as the refresh cookie is valid,
  // without the user ever seeing a 401 or a forced logout mid-run.
  const scheduleRefresh = useCallback(
    (accessToken: string) => {
      cancelScheduledRefresh();
      const expiryMs = getTokenExpiryMs(accessToken);
      if (expiryMs == null) return;
      const delay = Math.max(expiryMs - Date.now() - REFRESH_BUFFER_MS, MIN_REFRESH_DELAY_MS);
      refreshTimer.current = setTimeout(() => {
        refreshSession()
          .then((res) => {
            if (!adoptSlotOwner(res.user.id)) {
              clearSession();
              handleOwnerChanged();
              return;
            }
            setToken(res.accessToken);
            setUser(res.user);
            scheduleRefresh(res.accessToken);
          })
          .catch(() => clearSession());
      }, delay);
    },
    [cancelScheduledRefresh, clearSession],
  );

  // Signs out THIS tab's account only; the chooser then offers the others
  // (and sends to /login when none remain).
  const logout = useCallback(() => {
    clearSession();
    logoutRequest()
      .catch(() => undefined)
      .finally(() => window.location.assign('/accounts'));
  }, [clearSession]);

  const logoutAll = useCallback(() => {
    clearSession();
    logoutRequest({ all: true })
      .catch(() => undefined)
      .finally(() => window.location.assign('/login'));
  }, [clearSession]);

  const setSession = useCallback(
    (res: LoginResponse) => {
      setToken(res.accessToken);
      setUser(res.user);
      setSlotOwner(res.user.id);
      // Clear any stale org stored from a previous session so the next
      // GET /orgs isn't rejected by OrgContextGuard for a wrong X-Org-Id.
      setActiveOrgId(null);
      scheduleRefresh(res.accessToken);
    },
    [scheduleRefresh],
  );

  const login = useCallback(
    async (payload: LoginRequest) => {
      const res = await loginRequest(payload);
      if (!res.requiresTotp && res.accessToken && res.user) {
        setToken(res.accessToken);
        setUser(res.user);
        setSlotOwner(res.user.id);
        // Clear any stale org from a previous session before the org context
        // resolves, so OrgContextGuard doesn't 403 on the first GET /orgs.
        setActiveOrgId(null);
        scheduleRefresh(res.accessToken);
      }
      return res;
    },
    [scheduleRefresh],
  );

  // A reactive refresh (axios 401 interceptor) that also fails means the
  // refresh cookie itself is gone/expired — that's a genuine, final logout.
  useEffect(() => {
    setUnauthorizedHandler(() => clearSession());
    return () => setUnauthorizedHandler(null);
  }, [clearSession]);

  // On boot: the access token is in-memory only and never survives a reload,
  // so always attempt a silent refresh via the HttpOnly cookie. Failure just
  // means "not logged in" — not an error to surface.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await refreshSession();
        if (cancelled) return;
        adoptSlotOwner(res.user.id);
        setToken(res.accessToken);
        setUser(res.user);
        scheduleRefresh(res.accessToken);
      } catch {
        if (cancelled) return;
        setToken(null);
        // This tab's slot has no session. If the browser holds other
        // accounts, a protected page moves to one of them (or the chooser).
        if (!isPublicPath(stripSlot(window.location.pathname))) {
          const accounts = await fetchAccounts().catch(() => []);
          const target = bootRedirect({ slot: getSlot(), path: currentAppPath(), accounts });
          if (target && !cancelled) {
            window.location.replace(target);
            return; // stay "initializing" — the page is about to unload
          }
        }
      }
      if (!cancelled) setInitializing(false);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => cancelScheduledRefresh, [cancelScheduledRefresh]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      initializing,
      isAuthenticated: !!user,
      login,
      setSession,
      logout,
      logoutAll,
    }),
    [user, initializing, login, setSession, logout, logoutAll],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
